import * as React from 'react';

type PossibleRef<T> = React.RefCallback<T> | React.MutableRefObject<T | null> | null | undefined;

function setRef<T>(ref: PossibleRef<T>, value: T | null) {
  if (!ref) return;

  if ('current' in ref) {
    ref.current = value;

    return;
  }

  ref(value);
}

function composeRefs<T>(...refs: PossibleRef<T>[]): React.RefCallback<T> {
  return (node) => refs.forEach((ref) => setRef(ref, node));
}

function useComposedRefs<T>(...refs: PossibleRef<T>[]): React.RefCallback<T> {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return React.useCallback(composeRefs(...refs), refs);
}

function genericForwardRef<T, P>(
  render: (props: P, ref: React.ForwardedRef<T>) => React.ReactElement | null,
  displayName: string,
) {
  // SAFETY: React strips `ref` from props before calling render, so render receives exactly P without the ref.
  const component = React.forwardRef(render as React.ForwardRefRenderFunction<T, React.PropsWithoutRef<P>>);
  component.displayName = displayName;

  // SAFETY: a forwardRef component accepts its render props plus `ref`; generics are lost only in React's typings.
  return component as (props: P & React.RefAttributes<T>) => React.ReactElement | null;
}

export { genericForwardRef, useComposedRefs };
